import axios from 'axios';

// Centralized API client with fallback and error handling
class ApiClient {
  constructor() {
    this.baseURL = this.getBaseURL();
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  getBaseURL() {
    let url = process.env.REACT_APP_BACKEND_URL || '';

    if (url) {
      return url.endsWith('/') ? `${url}api/` : `${url}/api/`;
    }

    const fallback = '/api/';
    console.log('📡 API Client using Single-URL Proxy:', fallback);
    return fallback;
  }

  setupInterceptors() {
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config;
        if (!config) return Promise.reject(error);

        if (!config.__retryCount) config.__retryCount = 0;

        if (config.__retryCount >= 2) {
          return Promise.reject(error);
        }

        const shouldRetry =
          !error.response ||
          (error.response.status >= 500 && error.response.status <= 599);

        if (shouldRetry) {
          config.__retryCount += 1;
          const delay = Math.pow(2, config.__retryCount - 1) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.client(config);
        }

        return Promise.reject(error);
      }
    );
  }

  // Product endpoints (Public)
  async getProducts(params = {}) {
    const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    const response = await this.client.get('products', { params: filteredParams });
    return response.data;
  }

  async getProduct(id) {
    const response = await this.client.get(`products/${id}`);
    return response.data;
  }

  async getCategories() {
    const response = await this.client.get('categories');
    return response.data;
  }

  async getCategoryStats() {
    const response = await this.client.get('categories/stats');
    return response.data;
  }

  async getBestSellers(limit = 8) {
    const response = await this.client.get('products/best-sellers', { params: { limit } });
    return response.data;
  }

  async getNewArrivals(limit = 4) {
    const response = await this.client.get('products/new-arrivals', { params: { limit } });
    return response.data;
  }

  // Order endpoints (Public Checkout)
  async createOrder(orderData) {
    const response = await this.client.post('orders', orderData);
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
